import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Row,
  Modal,
  ModalBody,
  Label,
  ModalHeader,
  ModalFooter,
  FormFeedback,
  Button,
} from "reactstrap";
import * as moment from "moment";

import BreadCrumb from "../Components/Common/BreadCrumb";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import TableContainerComp from "Components/Common/TableContainerComp";
import { toast } from "react-toastify";
import SpinnerLoader from "Components/Common/Spinner/Spinner";
import useUserStore from "Store/UserStore/UserStore";

// Import a rich text editor - using ReactQuill as an example
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Import API service
import glossaryServiceObj from "ApiService/glossaryApiService";
import type { Glossary, CreateGlossaryPayload, UpdateGlossaryPayload } from "Interface/glossary";

const GlossaryPage = () => {
  const [glossaries, setGlossaries] = useState<Glossary[]>([]);
  const [glossaryObject, setCurrentGlossary] = useState<Glossary | null>(null);
  const [pageSize] = useState(10);
  const [glossaryPageIndex, setGlossaryPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCountOfPage, setTotalCountOfPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [modal, setModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [serialNumber, setSerialNumber] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [glossaryContent, setGlossaryContent] = useState("");

  const { currentUser } = useUserStore((state: any) => ({
    currentUser: state.currentUser,
  }));

  // ReactQuill modules configuration (simplified without image upload)
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link"],
        ["clean"],
      ],
    },
  }), []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link'
  ];

  // API functions
  const listingGlossaries = useCallback(async (filters: any) => {
    try {
      const response = await glossaryServiceObj.listing(filters);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching glossaries:', error);
      throw error;
    }
  }, []);

  const createGlossary = async (glossary: CreateGlossaryPayload) => {
    try {
      const response = await glossaryServiceObj.create(glossary);
      return response.data;
    } catch (error: any) {
      console.error('Error creating glossary:', error);
      throw error;
    }
  };

  const updateGlossary = async (glossary: UpdateGlossaryPayload) => {
    try {
      const response = await glossaryServiceObj.update(glossary);
      return response.data;
    } catch (error: any) {
      console.error('Error updating glossary:', error);
      throw error;
    }
  };

  const deleteGlossary = async (payload: { _id: string }) => {
    try {
      const response = await glossaryServiceObj.archive(payload);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting glossary:', error);
      throw error;
    }
  };

  const getRecords = useCallback(async (
    pageSize: number,
    pageIndex: number,
    search: string,
    category: string
  ) => {
    try {
      setLoading(true);
      const skip = pageIndex * pageSize;
      const limit = pageSize;
      
      const filters: any = {
        skip: skip,
        limit: limit,
        search: search,
        sortBy: 'createdAt desc'
      };

      // Add category filter if provided
      if (category) {
        filters.category = category;
      }

      console.log('🔍 Sending glossary request with filters:', filters);

      const res = await listingGlossaries(filters);
      const glossariesList = res?.data?.items || [];
      
      console.log('📄 Glossary response received:', {
        searchTerm: search,
        category: category,
        resultsCount: glossariesList.length,
        totalCount: res?.data?.count,
        fullResponse: res
      });
      
      const glossaryTotalCount = res?.data?.count || 0;
      const calculatedTotalPages = Math.ceil(glossaryTotalCount / pageSize);
      
      setTotalCountOfPage(glossaryTotalCount);
      setTotalPages(calculatedTotalPages);
      setGlossaries(glossariesList);
      return glossariesList;
    } catch (err: any) {
      console.error('❌ Error fetching glossaries:', err);
      toast.error(err?.message || err?.msg || "Error fetching glossaries");
      return [];
    } finally {
      setLoading(false);
    }
  }, [listingGlossaries]);

  useEffect(() => {
    getRecords(pageSize, glossaryPageIndex, search, categoryFilter);
  }, [getRecords, glossaryPageIndex, pageSize, search, categoryFilter]);

  const handleNextPage = () => {
    setGlossaryPageIndex((prevIndex) => {
      const newIndex = Math.min(prevIndex + 1, totalPages - 1);
      const newSerialNumber = newIndex * pageSize + 1;
      setSerialNumber(newSerialNumber);
      return newIndex;
    });
  };

  const handlePreviousPage = () => {
    setGlossaryPageIndex((prevIndex) => {
      const newIndex = Math.max(prevIndex - 1, 0);
      const newSerialNumber = newIndex * pageSize + 1;
      setSerialNumber(newSerialNumber);
      return newIndex;
    });
  };

  const toggle = useCallback(() => {
    setModal(!modal);
  }, [modal]);

  const toggleDeleteModal = useCallback(() => {
    setDeleteModal(!deleteModal);
  }, [deleteModal]);

  const handleGlossaryContentChange = (content: string) => {
    setGlossaryContent(content);
  };

  useEffect(() => {
    if (modal && !isEdit) {
      setCurrentGlossary(null);
      setGlossaryContent("");
      validation.resetForm();
    }
  }, [modal]);

  const handleGlossaryClicks = () => {
    setCurrentGlossary(null);
    setIsEdit(false);
    setIsEditMode(false);
    setEditingRecordId(null);
    setGlossaryContent("");
    validation.resetForm();
    toggle();
  };

  // Handle search input change
  const handleSearchInputChange = (event: any) => {
    setSearchInput(event.target.value);
  };

  // Handle search submission (button click or enter key)
  const handleSearchSubmit = () => {
    console.log('🔍 Search submitted:', searchInput);
    setSearch(searchInput);
    setGlossaryPageIndex(0);
    setSerialNumber(1);
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setCategoryFilter("");
    setGlossaryPageIndex(0);
    setSerialNumber(1);
  };

  // Handle category filter
  const handleCategoryFilter = (category: string) => {
    setCategoryFilter(category);
    setGlossaryPageIndex(0);
    setSerialNumber(1);
  };

  // Generate alphabet buttons for category filtering
  const alphabetButtons = useMemo(() => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    return alphabet.map(letter => (
      <Button
        key={letter}
        color={categoryFilter === letter ? "primary" : "light"}
        size="sm"
        className="me-1 mb-1"
        onClick={() => handleCategoryFilter(letter)}
        disabled={loading}
      >
        {letter}
      </Button>
    ));
  }, [categoryFilter, loading]);

  const validation: any = useFormik({
    enableReinitialize: true,

    initialValues: {
      glossaryTitle: glossaryObject?.glossaryTitle || "",
      slug: glossaryObject?.slug || "",
      metaTitle: glossaryObject?.metaTitle || "",
      metaDescription: glossaryObject?.metaDescription || "",
      author: glossaryObject?.author || "",
      publishedDate: glossaryObject?.publishedDate ? moment(glossaryObject.publishedDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      status: glossaryObject?.status || 'draft',
    },
    validationSchema: Yup.object({
      glossaryTitle: Yup.string()
        .required("Please Enter Glossary Title")
        .min(5, "Glossary title must be at least 5 characters")
        .max(200, "Glossary title must be at most 200 characters"),
      slug: Yup.string()
        .required("Please Enter Slug")
        .min(3, "Slug must be at least 3 characters")
        .matches(/^[a-z0-9-]+$/, "Slug must contain only lowercase letters, numbers, and hyphens"),
      metaTitle: Yup.string()
        .required("Please Enter Meta Title")
        .min(10, "Meta title must be at least 10 characters")
        .max(60, "Meta title must be at most 60 characters"),
      metaDescription: Yup.string()
        .required("Please Enter Meta Description")
        .min(120, "Meta description must be at least 120 characters")
        .max(500, "Meta description must be at most 500 characters"),
      author: Yup.string()
        .required("Please Enter Author Name")
        .min(2, "Author name must be at least 2 characters"),
      publishedDate: Yup.date().required("Please Select Publish Date"),
      status: Yup.string()
        .oneOf(['draft', 'published', 'archived'], "Status must be either draft, published, or archived")
        .required("Please Select Status"),
    }),
    onSubmit: async (values: any) => {
      if (!glossaryContent || glossaryContent.trim().length < 100) {
        toast.error('Glossary content must be at least 100 characters');
        return;
      }

      setIsSubmitting(true);
      setLoading(true);

      try {
        const glossaryData: any = {
          glossaryTitle: values.glossaryTitle.trim(),
          metaTitle: values.metaTitle.trim(),
          slug: values.slug.trim(),
          metaDescription: values.metaDescription.trim(),
          glossaryContent: glossaryContent,
          author: values.author.trim(),
          publishedDate: new Date(values.publishedDate).toISOString(),
          status: values.status,
          tags: glossaryObject?.tags || [],
          category: glossaryObject?.category || '',
          active: true,
          isArchived: false,
        };

        let response;
        if (isEditMode && glossaryObject?._id) {
          glossaryData._id = glossaryObject._id;
          response = await updateGlossary(glossaryData);
        } else {
          response = await createGlossary(glossaryData);
        }

        if (response) {
          toast.success(isEditMode ? 'Glossary updated successfully' : 'Glossary created successfully');
          await getRecords(pageSize, glossaryPageIndex, search, categoryFilter);
          setModal(false);
          validation.resetForm();
          setGlossaryContent("");
          setIsEditMode(false);
          setEditingRecordId(null);
          setCurrentGlossary(null);
        }
      } catch (err: any) {
        console.error('Error saving glossary:', err);
        toast.error(err?.message || err?.msg || `Error ${isEditMode ? 'updating' : 'creating'} glossary`);
      } finally {
        setIsSubmitting(false);
        setLoading(false);
      }
    },
  });

  const handleGlossaryClick = useCallback(
    (arg: Glossary) => {
      setCurrentGlossary(arg);
      setIsEdit(true);
      setIsEditMode(true);
      setEditingRecordId(arg._id || null);
      
      setGlossaryContent(arg.glossaryContent || "");
      
      toggle();
    },
    [toggle]
  );

  const handleDeleteClick = useCallback(
    (arg: Glossary) => {
      setCurrentGlossary(arg);
      toggleDeleteModal();
    },
    [toggleDeleteModal]
  );

  const confirmDelete = async () => {
    if (!glossaryObject?._id) return;
    
    try {
      setLoading(true);
      await deleteGlossary({ _id: glossaryObject._id });
      await getRecords(pageSize, glossaryPageIndex, search, categoryFilter);
      toggleDeleteModal();
      toast.success("Glossary deleted successfully!");
      setCurrentGlossary(null);
    } catch (err: any) {
      console.error('Error deleting glossary:', err);
      toast.error(err?.message || err?.msg || "Error deleting glossary");
    } finally {
      setLoading(false);
    }
  };

  const handleValidDate = (date: any) => {
    const date1 = moment(new Date(date)).format("DD-MM-YYYY");
    return date1;
  };

  const columns = useMemo(
    () => [
      {
        Header: "No.",
        accessor: (row: any, index: any) => index + 1 + glossaryPageIndex * pageSize,
        id: "serialNumberColumn",
        filterable: false,
        width: 60,
      },
      {
        Header: "Title",
        accessor: "glossaryTitle",
        filterable: false,
        Cell: (glossaries: any) => (
          <>
            <div className="d-flex align-items-center">
              <div>
                {glossaries.row.original.glossaryTitle && glossaries.row.original.glossaryTitle.length > 40 
                  ? glossaries.row.original.glossaryTitle.substring(0, 40) + '...'
                  : glossaries.row.original.glossaryTitle
                }
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Slug",
        accessor: "slug",
        filterable: false,
        Cell: (glossaries: any) => (
          <>
            <div className="d-flex align-items-center">
              <div>
                {glossaries.row.original.slug && glossaries.row.original.slug.length > 40 
                  ? glossaries.row.original.slug.substring(0, 40) + '...'
                  : glossaries.row.original.slug || '-'
                }
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        filterable: false,
        Cell: (cell: any) => {
          const statusConfig: any = {
            'draft': { class: 'bg-secondary', label: 'Draft' },
            'published': { class: 'bg-success', label: 'Published' },
            'archived': { class: 'bg-warning', label: 'Archived' }
          };
          const config = statusConfig[cell.row.original.status] || statusConfig.draft;
          return (
            <span className={`badge ${config.class}`}>
              {config.label}
            </span>
          );
        },
      },
      {
        Header: "Published At",
        accessor: "publishedDate",
        filterable: false,
        Cell: (cell: any) => (
          <>{cell.row.original.publishedDate ? handleValidDate(cell.row.original.publishedDate) : '-'}</>
        ),
      },
      {
        Header: "Author",
        accessor: "author",
        filterable: false,
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          return (
            <ul className="list-inline hstack mb-0">
              <li className="list-inline-item" title="Edit">
                <Link
                  className="text-success"
                  to="#"
                  onClick={() => {
                    const glossaryData = cellProps.row.original;
                    handleGlossaryClick(glossaryData);
                  }}
                >
                  <i className="mdi mdi-pencil font-size-18"></i>
                </Link>
              </li>
              <li className="list-inline-item" title="Delete">
                <Link
                  className="text-danger"
                  to="#"
                  onClick={() => {
                    const glossaryData = cellProps.row.original;
                    handleDeleteClick(glossaryData);
                  }}
                >
                  <i className="mdi mdi-delete font-size-18"></i>
                </Link>
              </li>
            </ul>
          );
        },
      },
    ],
    [handleGlossaryClick, handleDeleteClick, glossaryPageIndex, pageSize]
  );

  document.title = "Glossary";

  return (
    <React.Fragment>
      {loading && <SpinnerLoader />}
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Glossary" pageTitle="Glossary" pageLink="/glossary" />
          
          <Row>
            <Col lg={14}>
              <Card id="glossaryList">
                <CardHeader className="border-0">
                  <div className="d-md-flex align-items-center">
                    <h5 className="card-title mb-3 mb-md-0 flex-grow-1">
                      Glossary Terms ({totalCountOfPage})
                    </h5>
                    <div className="flex-shrink-0">
                      <div className="d-flex gap-1 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-success add-btn"
                          id="create-btn"
                          onClick={() => {
                            setIsEdit(false);
                            setIsEditMode(false);
                            setEditingRecordId(null);
                            setCurrentGlossary(null);
                            setGlossaryContent("");
                            validation.resetForm();
                            toggle();
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          Add Glossary Term
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardBody className="border border-dashed border-end-0 border-start-0">
                  <Row className="g-4 align-items-center">
                    <Col sm={4}>
                      <div className="search-box">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder="Search for glossary terms..."
                          value={searchInput}
                          onChange={handleSearchInputChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col sm={2}>
                      <Button
                        color="primary"
                        className="w-100"
                        onClick={handleSearchSubmit}
                        disabled={loading}
                      >
                        <i className="ri-search-line me-1"></i> Search
                      </Button>
                    </Col>
                    <Col sm={2}>
                      <Button
                        color="light"
                        className="w-100"
                        onClick={handleClearSearch}
                        disabled={loading || (!searchInput && !search && !categoryFilter)}
                      >
                        <i className="ri-close-line me-1"></i> Clear
                      </Button>
                    </Col>
                    <Col sm={4}>
                      <div className="text-muted">
                        Showing {glossaries.length} of {totalCountOfPage} terms 
                        {search && (
                          <span> , search results for "{search}"</span>
                        )}
                        {categoryFilter && (
                          <span> , filtered by "{categoryFilter}"</span>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Alphabet Filter */}
                  <Row className="mt-3">
                    <Col lg={12}>
                      <div className="d-flex flex-wrap justify-content-center">
                        <small className="text-muted me-2 align-self-center">Filter by letter:</small>
                        {alphabetButtons}
                      </div>
                    </Col>
                  </Row>
                </CardBody>

                <CardBody className="pt-0">
                  <div>
                    {glossaries && glossaries.length ? (
                      <TableContainerComp
                        columns={columns}
                        data={glossaries || []}
                        isGlobalFilter={false}
                        isAddUserList={false}
                        customPageSize={10}
                        className="custom-header-css"
                        divClass="table-responsive table-card"
                        tableClass="align-middle"
                        theadClass="table-light text-muted"
                        handleLeadClick={handleGlossaryClicks}
                        isLeadsFilter={false}
                        totalCount={totalCountOfPage}
                        handleNextPage={handleNextPage}
                        handlePreviousPage={handlePreviousPage}
                        getRecords={() => getRecords(pageSize, glossaryPageIndex, search, categoryFilter)}
                        setUser={setGlossaries}
                        setUserPageIndex={setGlossaryPageIndex}
                        userPageIndex={glossaryPageIndex}
                      />
                    ) : (
                      <div className="text-center py-4">
                        <div className="fw-bold unavailable-msg">No Glossary Terms Available</div>
                        <p className="mt-2 non-record-text">
                          {loading ? 'Loading glossary terms...' : search || categoryFilter ? `No glossary terms found${search ? ` for "${search}"` : ''}${categoryFilter ? ` in category "${categoryFilter}"` : ''}` : "It looks like you haven't set up any glossary terms yet. Add a glossary term to start building your knowledge base."}
                        </p>
                        {!loading && (
                          <button 
                            className="btn btn-success mt-3"
                            onClick={() => {
                              setIsEdit(false);
                              setIsEditMode(false);
                              setEditingRecordId(null);
                              setCurrentGlossary(null);
                              setGlossaryContent("");
                              validation.resetForm();
                              toggle();
                            }}
                          >
                            Add a New Glossary Term
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Add/Edit Glossary Modal */}
                  <Modal
                    className="modal-xl"
                    id="showModal"
                    isOpen={modal}
                    toggle={toggle}
                    centered
                    size="xl"
                  >
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {isEditMode ? "EDIT GLOSSARY TERM" : "ADD GLOSSARY TERM"}
                    </ModalHeader>
                    <Form
                      className="tablelist-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        <Row className="g-3">
                          {/* Glossary Title & Slug */}
                          <Col md={6}>
                            <Label htmlFor="glossaryTitle" className="form-label">
                              Glossary Title *
                            </Label>
                            <Input
                              name="glossaryTitle"
                              id="glossaryTitle"
                              className="form-control"
                              placeholder="Enter glossary title"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.glossaryTitle || ""}
                              invalid={
                                validation.touched.glossaryTitle &&
                                validation.errors.glossaryTitle
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.glossaryTitle &&
                              validation.errors.glossaryTitle ? (
                              <FormFeedback type="invalid">
                                {validation.errors.glossaryTitle}
                              </FormFeedback>
                            ) : null}
                          </Col>
                          <Col md={6}>
                            <Label htmlFor="slug" className="form-label">
                              Slug *
                            </Label>
                            <Input
                              name="slug"
                              id="slug"
                              className="form-control"
                              placeholder="Enter slug (URL friendly)"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.slug || ""}
                              invalid={
                                validation.touched.slug &&
                                validation.errors.slug
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.slug &&
                              validation.errors.slug ? (
                              <FormFeedback type="invalid">
                                {validation.errors.slug}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          {/* Meta Title & Author */}
                          <Col md={6}>
                            <Label htmlFor="metaTitle" className="form-label">
                              Meta Title *
                            </Label>
                            <Input
                              name="metaTitle"
                              id="metaTitle"
                              className="form-control"
                              placeholder="Enter meta title"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.metaTitle || ""}
                              invalid={
                                validation.touched.metaTitle &&
                                validation.errors.metaTitle
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.metaTitle &&
                              validation.errors.metaTitle ? (
                              <FormFeedback type="invalid">
                                {validation.errors.metaTitle}
                              </FormFeedback>
                            ) : null}
                          </Col>
                          <Col md={6}>
                            <Label htmlFor="author" className="form-label">
                              Author Name *
                            </Label>
                            <Input
                              name="author"
                              id="author"
                              className="form-control"
                              placeholder="Enter author name"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.author || ""}
                              invalid={
                                validation.touched.author &&
                                validation.errors.author
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.author &&
                              validation.errors.author ? (
                              <FormFeedback type="invalid">
                                {validation.errors.author}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          {/* Meta Description */}
                          <Col lg={12}>
                            <Label htmlFor="metaDescription" className="form-label">
                              Meta Description *
                            </Label>
                            <Input
                              name="metaDescription"
                              id="metaDescription"
                              className="form-control"
                              placeholder="Enter meta description"
                              type="textarea"
                              rows="3"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.metaDescription || ""}
                              invalid={
                                validation.touched.metaDescription &&
                                validation.errors.metaDescription
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.metaDescription &&
                              validation.errors.metaDescription ? (
                              <FormFeedback type="invalid">
                                {validation.errors.metaDescription}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          {/* Publish Date & Status */}
                          <Col md={6}>
                            <Label htmlFor="publishedDate" className="form-label">
                              Publish Date *
                            </Label>
                            <Input
                              name="publishedDate"
                              id="publishedDate"
                              className="form-control"
                              type="date"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.publishedDate ? moment(validation.values.publishedDate).format('YYYY-MM-DD') : ''}
                              invalid={
                                validation.touched.publishedDate &&
                                validation.errors.publishedDate
                              }
                              disabled={isSubmitting}
                            />
                            {validation.touched.publishedDate &&
                              validation.errors.publishedDate ? (
                              <FormFeedback type="invalid">
                                {validation.errors.publishedDate}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          <Col md={6}>
                            <Label htmlFor="status" className="form-label">
                              Status *
                            </Label>
                            <Input
                              name="status"
                              id="status"
                              className="form-control"
                              type="select"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.status || ""}
                              invalid={
                                validation.touched.status &&
                                validation.errors.status
                              }
                              disabled={isSubmitting}
                            >
                              <option value="draft">Draft</option>
                              <option value="published">Published</option>
                              <option value="archived">Archived</option>
                            </Input>
                            {validation.touched.status &&
                              validation.errors.status ? (
                              <FormFeedback type="invalid">
                                {validation.errors.status}
                              </FormFeedback>
                            ) : null}
                          </Col>

                          {/* Glossary Content - Rich Text Editor */}
                          <Col lg={12}>
                            <Label htmlFor="glossaryContent" className="form-label">
                              Glossary Content *
                            </Label>
                            <div className="text-editor-container">
                              <ReactQuill
                                value={glossaryContent}
                                onChange={handleGlossaryContentChange}
                                modules={modules}
                                formats={formats}
                                placeholder="Write your glossary content here..."
                                style={{ height: '300px', marginBottom: '50px' }}
                              />
                            </div>
                            {glossaryContent && glossaryContent.trim().length < 100 && (
                              <div className="text-danger small mt-1">
                                Glossary content must be at least 100 characters (currently {glossaryContent.trim().length})
                              </div>
                            )}
                          </Col>
                        </Row>
                      </ModalBody>
                      <ModalFooter>
                        <div className="hstack gap-2 justify-content-end">
                          <button
                            type="button"
                            className="btn btn-light"
                            onClick={() => {
                              setModal(false);
                              validation.resetForm();
                              setGlossaryContent("");
                              setIsEditMode(false);
                              setEditingRecordId(null);
                              setCurrentGlossary(null);
                            }}
                            disabled={isSubmitting}
                          >
                            Close
                          </button>
                          <button
                            type="submit"
                            className="btn btn-success"
                            disabled={isSubmitting || !glossaryContent || glossaryContent.trim().length < 100}
                          >
                            {isSubmitting ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {isEditMode ? "Updating..." : "Creating..."}
                              </>
                            ) : (
                              <>
                                <i className="ri-save-line align-bottom me-1"></i>
                                {isEditMode ? "Update Glossary" : "Create Glossary"}
                              </>
                            )}
                          </button>
                        </div>
                      </ModalFooter>
                    </Form>
                  </Modal>

                  {/* Delete Modal */}
                  <Modal
                    isOpen={deleteModal}
                    toggle={toggleDeleteModal}
                    centered
                  >
                    <ModalHeader toggle={toggleDeleteModal}>
                      Delete Glossary Term
                    </ModalHeader>
                    <ModalBody>
                      <div className="text-center">
                        <i className="ri-delete-bin-line text-danger" style={{ fontSize: '48px' }}></i>
                        <h4 className="mt-3">Are you sure?</h4>
                        <p className="text-muted">
                          You are about to delete the glossary term "{glossaryObject?.glossaryTitle}". This action cannot be undone.
                        </p>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <div className="hstack gap-2 justify-content-center w-100">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={toggleDeleteModal}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={confirmDelete}
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Deleting...
                            </>
                          ) : (
                            "Delete Glossary Term"
                          )}
                        </button>
                      </div>
                    </ModalFooter>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default GlossaryPage;